import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { getClubSports, type ClubSport } from "../../services/api";
import { createInvitation } from "../../services/invitations";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { SectionCard } from "../../components/admin/AdminCard";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import PendingInvitations from "../../components/club/PendingInvitations";
import { grantClubRole } from "../../services/api";

type StaffMember = {
  user: { id: string; email: string; full_name: string | null; status: string };
  role: "coach" | "coordinator";
  joined_at: string;
  sports: { id: string; name: string }[];
};

const inputClass =
  "w-full px-3 py-2 text-sm border rounded-lg border-slate-300 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500";

/**
 * Club staff: coordinators and coaches, and which sports each one covers.
 *
 * A coordinator runs one sport (or several) on the club admin's behalf — the
 * arrangement large clubs already have and small ones do not need, so nothing
 * here is required to run a club.
 *
 * Sports are what scope a coordinator, so a coordinator with none assigned sees
 * nothing at all. That is deliberate and worth saying on screen, because an
 * empty dashboard otherwise reads as a bug.
 */
export default function ClubStaff() {
  const { activeClubId, coordinatorsEnabled } = useAuth();

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [sports, setSports] = useState<ClubSport[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"coordinator" | "coach">(
    coordinatorsEnabled ? "coordinator" : "coach"
  );
  const [inviteSportId, setInviteSportId] = useState("");
  const [inviting, setInviting] = useState(false);

  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [editSports, setEditSports] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (!activeClubId) return;
    setLoading(true);
    try {
      const [s, sp] = await Promise.all([
        api.get<{ staff: StaffMember[] }>(`/clubs/${activeClubId}/staff`),
        getClubSports(activeClubId).catch(() => [] as ClubSport[]),
      ]);
      setStaff(s.data.staff || []);
      setSports(sp);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut încărca personalul clubului.");
    }
    setLoading(false);
  }, [activeClubId]);

  useEffect(() => {
    load();
  }, [load]);

  // A club that switched coordinators off while this page was open must not be
  // left with "Coordonator" pre-selected in the invite form.
  useEffect(() => {
    if (!coordinatorsEnabled) setInviteRole("coach");
  }, [coordinatorsEnabled]);

  const coordinators = useMemo(() => staff.filter((s) => s.role === "coordinator"), [staff]);
  const coaches = useMemo(() => staff.filter((s) => s.role === "coach"), [staff]);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClubId || !inviteEmail.trim()) return;
    if (inviteRole === "coordinator" && !inviteSportId) {
      toast.error("Alege sportul pe care îl coordonează.");
      return;
    }
    setInviting(true);
    try {
      await createInvitation({
        email: inviteEmail.trim(),
        role_code: inviteRole,
        club_id: activeClubId,
        sport_id: inviteSportId || undefined,
      });
      toast.success("Invitație trimisă.");
      setInviteEmail("");
      setInviteSportId("");
      setInviteOpen(false);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut trimite invitația.");
    }
    setInviting(false);
  };

  // Making an existing coach a coordinator. No invitation: they already work
  // here and already confirmed their email, so asking them to accept a new
  // invitation to the same club is a round trip for nothing.
  const [promoting, setPromoting] = useState<StaffMember | null>(null);
  const [promoteSportId, setPromoteSportId] = useState("");

  const promote = async () => {
    if (!activeClubId || !promoting) return;
    if (!promoteSportId) {
      toast.error("Un coordonator coordonează un sport — alege-l.");
      return;
    }
    setSaving(promoting.user.id);
    try {
      const res = await grantClubRole(activeClubId, {
        user_id: promoting.user.id,
        role_code: "coordinator",
        sport_id: promoteSportId,
      });
      toast.success(res.message);
      setPromoting(null);
      setPromoteSportId("");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut acorda rolul.");
    }
    setSaving(null);
  };

  const openEdit = (member: StaffMember) => {
    setEditing(member);
    setEditSports(member.sports.map((s) => s.id));
  };

  const saveSports = async () => {
    if (!activeClubId || !editing) return;
    setSaving(editing.user.id);
    try {
      await api.put(`/clubs/${activeClubId}/staff/${editing.user.id}/sports`, {
        role: editing.role,
        sport_ids: editSports,
      });
      toast.success("Sporturile au fost actualizate.");
      setEditing(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut salva sporturile.");
    }
    setSaving(null);
  };

  const remove = async (member: StaffMember) => {
    if (!activeClubId) return;
    if (
      !window.confirm(
        `Scoți ${member.user.full_name || member.user.email} din rolul de ${
          member.role === "coordinator" ? "coordonator" : "antrenor"
        }? Contul rămâne activ.`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/clubs/${activeClubId}/staff/${member.user.id}?role=${member.role}`);
      toast.success("Rol încheiat.");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut încheia rolul.");
    }
  };

  const renderList = (rows: StaffMember[], emptyText: string) => {
    if (rows.length === 0) return <p className="px-5 py-6 text-sm text-slate-500">{emptyText}</p>;
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs font-semibold tracking-wide uppercase text-slate-400">
              <th className="px-5 py-2 text-left">Nume</th>
              <th className="py-2 pr-4 text-left">Email</th>
              <th className="py-2 pr-4 text-left">Sporturi</th>
              <th className="py-2 pr-5 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={`${m.user.id}-${m.role}`} className="border-t border-slate-100">
                <td className="px-5 py-3">
                  {/* Straight to the person: their page is where you see their
                      roles, teams and account state. */}
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/users/${m.user.id}`)}
                    className="font-semibold text-left text-slate-800 hover:text-indigo-600"
                  >
                    {m.user.full_name || m.user.email}
                  </button>
                </td>
                <td className="py-3 pr-4 break-all text-slate-500">{m.user.email}</td>
                <td className="py-3 pr-4">
                  {m.sports.length === 0 ? (
                    <span className="text-xs font-semibold text-amber-600">
                      {m.role === "coordinator" ? "Niciun sport — nu vede nimic" : "Niciun sport"}
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {m.sports.map((sp) => (
                        <span
                          key={sp.id}
                          className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700"
                        >
                          {sp.name}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-3 pr-5 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => openEdit(m)}
                    className="px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    Sporturi
                  </button>
                  {coordinatorsEnabled && m.role === "coach" && !coordinators.some((c) => c.user.id === m.user.id) && (
                    <button
                      type="button"
                      onClick={() => {
                        setPromoting(m);
                        // Their own sport, when they have exactly one — that is
                        // almost always the sport they would coordinate.
                        setPromoteSportId(m.sports.length === 1 ? m.sports[0].id : "");
                      }}
                      className="px-3 py-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700"
                    >
                      Fă coordonator
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(m)}
                    className="px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-rose-600"
                  >
                    Scoate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <ClubAdminLayout>
      <div className="mx-auto max-w-[1500px] space-y-6">
        <AdminPageHeader
          title={coordinatorsEnabled ? "Coordonatori și antrenori" : "Antrenori și sporturi"}
          subtitle="Cine răspunde de fiecare sport din club."
          icon={<ClubNavIcon id="coaches" className="w-6 h-6" />}
          actions={<AdminButton onClick={() => setInviteOpen(true)}>Invită</AdminButton>}
        />

        {!coordinatorsEnabled && (
          <div className="px-4 py-3 text-sm border rounded-xl bg-slate-50 border-slate-200 text-slate-600">
            Clubul nu folosește coordonatori. Dacă ai nevoie de cineva care să răspundă de un
            singur sport, activezi rolul din <strong>Setări club</strong>.
          </div>
        )}

        {sports.length === 0 && !loading && (
          <div className="px-4 py-3 text-sm border rounded-xl bg-amber-50 border-amber-200 text-amber-800">
            Clubul nu are sporturi setate. Coordonatorii se alocă pe sporturi, deci alege-le întâi
            în <strong>Setări club</strong>.
          </div>
        )}

        {/* Everyone the club is still waiting on — whoever sent the
            invitation. Activating one here skips the email entirely. */}
        <PendingInvitations
          clubId={activeClubId}
          roles={["coordinator", "coach"]}
          onChanged={load}
        />

        {loading ? (
          <p className="text-sm text-slate-500">Se încarcă...</p>
        ) : (
          <>
            {coordinatorsEnabled && (
              <SectionCard
                title={`Coordonatori (${coordinators.length})`}
                subtitle="Opțional — cluburile mari deleagă câte un sport fiecăruia."
              >
                {renderList(
                  coordinators,
                  "Niciun coordonator. Dacă ești un club mic, nu ai nevoie de niciunul."
                )}
              </SectionCard>
            )}

            <SectionCard
              title={`Antrenori (${coaches.length})`}
              subtitle={
                coordinatorsEnabled
                  ? "Un antrenor de aici poate fi făcut coordonator direct, fără invitație."
                  : undefined
              }
            >
              {renderList(coaches, "Niciun antrenor încă.")}
            </SectionCard>
          </>
        )}

        {/* --------------------------------------------- make coordinator -- */}
        {promoting && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="w-full max-w-md p-5 space-y-4 bg-white rounded-2xl">
              <h2 className="text-lg font-bold text-slate-800">Fă coordonator</h2>
              <p className="text-sm text-slate-500">
                {promoting.user.full_name || promoting.user.email} rămâne antrenor și devine și
                coordonator. Nu primește nicio invitație — e deja în club.
              </p>

              <div>
                <label className="block mb-1 text-xs font-semibold text-slate-500">
                  Sportul coordonat *
                </label>
                <select
                  value={promoteSportId}
                  onChange={(e) => setPromoteSportId(e.target.value)}
                  className="w-full px-3 py-2 text-base bg-white border rounded-lg border-slate-300 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Alege sportul...</option>
                  {sports.map((sp) => (
                    <option key={sp.id} value={sp.id}>{sp.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-400">
                  Poți adăuga alte sporturi după aceea, din butonul „Sporturi".
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setPromoting(null)}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800"
                >
                  Anulează
                </button>
                <button
                  type="button"
                  onClick={promote}
                  disabled={!promoteSportId || saving === promoting.user.id}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
                >
                  {saving === promoting.user.id ? "Se salvează..." : "Fă coordonator"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------ invite -- */}
        {inviteOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <form onSubmit={invite} className="w-full max-w-md p-5 space-y-4 bg-white rounded-2xl">
              <h2 className="text-lg font-bold text-slate-800">Invită în club</h2>

              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700" htmlFor="staff-role">
                  Rol
                </label>
                <select
                  id="staff-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as "coordinator" | "coach")}
                  className={inputClass}
                >
                  {coordinatorsEnabled && <option value="coordinator">Coordonator</option>}
                  <option value="coach">Antrenor</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700" htmlFor="staff-email">
                  Email
                </label>
                <input
                  id="staff-email"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="nume@exemplu.ro"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700" htmlFor="staff-sport">
                  Sport {inviteRole === "coordinator" && <span className="text-rose-500">*</span>}
                </label>
                <select
                  id="staff-sport"
                  value={inviteSportId}
                  onChange={(e) => setInviteSportId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">— Alege —</option>
                  {sports.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-400">
                  {inviteRole === "coordinator"
                    ? "Coordonatorul vede doar echipele, antrenorii și sportivii acestui sport."
                    : "Opțional pentru antrenori — îl poți completa mai târziu."}
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setInviteOpen(false)}
                  className="flex-1 py-2.5 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="flex-1 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-60"
                >
                  {inviting ? "Se trimite..." : "Trimite invitația"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* -------------------------------------------------- edit sports -- */}
        {editing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="w-full max-w-md p-5 space-y-4 bg-white rounded-2xl">
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  Sporturi — {editing.user.full_name || editing.user.email}
                </h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  {editing.role === "coordinator"
                    ? "Coordonatorul vede doar sporturile bifate."
                    : "Sportul antrenorului se aplică automat echipelor pe care le creează."}
                </p>
              </div>

              {sports.length === 0 ? (
                <p className="text-sm text-amber-600">
                  Clubul nu are sporturi setate. Alege-le în Setări club.
                </p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {sports.map((sp) => {
                    const checked = editSports.includes(sp.id);
                    return (
                      <label
                        key={sp.id}
                        className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg cursor-pointer text-slate-700 hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setEditSports((prev) =>
                              checked ? prev.filter((x) => x !== sp.id) : [...prev, sp.id]
                            )
                          }
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        {sp.name}
                      </label>
                    );
                  })}
                </div>
              )}

              {editing.role === "coordinator" && editSports.length === 0 && (
                <p className="px-3 py-2 text-xs rounded-lg bg-amber-50 text-amber-800">
                  Fără niciun sport bifat, acest coordonator nu va vedea nimic în club.
                </p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="flex-1 py-2.5 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600"
                >
                  Anulează
                </button>
                <button
                  type="button"
                  onClick={saveSports}
                  disabled={saving === editing.user.id}
                  className="flex-1 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-60"
                >
                  {saving === editing.user.id ? "Se salvează..." : "Salvează"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ClubAdminLayout>
  );
}
